import type { TApplicantStatus } from '@/services/applicants';
import type { TArchetype } from '@/services/scoring';

import type { TSegmentedOption } from 'components/ui/SegmentedToggle';

const ARCHETYPES: readonly TArchetype[] = ['A', 'B+', 'B-', 'C'];

const ARCHETYPE_OPTIONS: TSegmentedOption[] = ARCHETYPES.map(archetype => ({
  value: archetype,
  label: archetype,
}));

const SCORE_STATUSES: readonly TApplicantStatus[] = [
  'interview_scheduled',
  'verification_pending',
  'pending_final_approval',
  'approved_waitlisted',
  'activation_ready',
  'active',
];

const SCORE_MIN = 0;

const SCORE_MAX = 10;

const SCORE_DIGITS_MAX = 3;

/**
 * The four criteria the frame draws, and what each is worth.
 *
 * **They are labels, not inputs.** Nothing is typed into them, nothing is
 * stored against them and no arithmetic reads them — the composite is the
 * number the recruiter types, exactly as before. What the rows do is put the
 * weighting in front of the person doing the weighing, which is the whole of
 * PRD §248's "guided criteria" and all this card can honestly claim: the
 * weights the PRD calls for are not enumerated anywhere and need calibration
 * data (§13.1), so an equal share is a placeholder the owner set, not a
 * formula anyone derived.
 *
 * Age and intent are not rows. The PRD makes age a modifier with its own table
 * and intent a judgement, and neither is a share of the same pie.
 */
const SCORE_CRITERIA = [
  { id: 'attractiveness', label: 'Attractiveness', weight: '×0.2' },
  { id: 'economic', label: 'Economic', weight: '×0.2' },
  { id: 'lifestyle', label: 'Lifestyle', weight: '×0.2' },
  { id: 'social', label: 'Social validation', weight: '×0.2' },
] as const;

const SCORING_COPY = {
  title: 'Quality score',
  scale: '/ 10',
  scoreLabel: 'Score',
  interviewRow: 'Interview boost',
  pending: '— pending',
  scorePlaceholder: '0.0',
  archetypeTitle: 'Archetype',
  saved: 'Assessment saved',
  empty: '—',
  privacy: 'Never visible to the applicant.',
} as const;

export {
  ARCHETYPE_OPTIONS,
  ARCHETYPES,
  SCORE_CRITERIA,
  SCORE_DIGITS_MAX,
  SCORE_MAX,
  SCORE_MIN,
  SCORE_STATUSES,
  SCORING_COPY,
};
